import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './mailBook.css';

let nextId = 0;

const SortableEmailItem = ({ id, email, isDefault, onChange, onToggleDefault, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="email-input-container">
      <button
        type="button"
        className="drag-handle"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        ⋮⋮
      </button>
      <input
        type="checkbox"
        checked={isDefault}
        onChange={e => onToggleDefault(id, e.target.checked)}
        className="email-default-checkbox"
        title="Par défaut"
      />
      <input
        type="email"
        name="email"
        value={email}
        onChange={e => onChange(id, e.target.value)}
        className="email-input"
        placeholder="email@example.com"
      />
      <button
        type="button"
        className="delete-button"
        onClick={() => onDelete(id)}
        aria-label="Delete email"
      >
        ×
      </button>
    </div>
  );
};

const MailBook = ({ email_book, default_emails = [], onClose, onConfirm, onCancel }) => {
  const [emailList, setEmailList] = useState(() => {
    // Normaliser default_emails (trim tous les emails)
    const normalizedDefaults = Array.isArray(default_emails)
      ? default_emails.map(e => (typeof e === 'string' ? e.trim() : e))
      : [];

    const emails = email_book
      .split(',')
      .filter(e => e.trim() !== '')
      .map(email => {
        const trimmedEmail = email.trim();
        const isDefault = normalizedDefaults.includes(trimmedEmail);
        return {
          id: `email-${nextId++}`,
          value: trimmedEmail,
          isDefault,
        };
      });
    return emails;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onTriggerConfirm = e => {
    e.preventDefault();
    const validEmails = emailList.filter(item => item.value.trim() !== '');
    const email_book_csv = validEmails.map(item => item.value.trim()).join(',');
    const default_emails_list = validEmails
      .filter(item => item.isDefault)
      .map(item => item.value.trim());
    onConfirm({ email_book: email_book_csv, default_emails: default_emails_list });
  };

  const handleInputChange = (id, value) => {
    setEmailList(items => items.map(item => (item.id === id ? { ...item, value } : item)));
  };

  const handleToggleDefault = (id, checked) => {
    setEmailList(items =>
      items.map(item => (item.id === id ? { ...item, isDefault: checked } : item))
    );
  };

  const handleAddInput = () => {
    setEmailList([...emailList, { id: `email-${nextId++}`, value: '', isDefault: false }]);
  };

  const handleDeleteInput = id => {
    setEmailList(items => items.filter(item => item.id !== id));
  };

  const handleDragEnd = event => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setEmailList(items => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <div className="overlay">
      <div className="mailbook">
        <span className="overlay-close" onClick={onClose}>
          &times;
        </span>
        <p>Emails supplémentaires</p>
        <div className="email-inputs">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={emailList.map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {emailList.map(item => (
                <SortableEmailItem
                  key={item.id}
                  id={item.id}
                  email={item.value}
                  isDefault={item.isDefault}
                  onChange={handleInputChange}
                  onToggleDefault={handleToggleDefault}
                  onDelete={handleDeleteInput}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
        <div className="addbutton-container">
          <button onClick={handleAddInput}>+</button>
        </div>
        <div className="mailbook-buttons">
          <button onClick={onTriggerConfirm}>Valider</button>
          <button onClick={onCancel}>Annuler</button>
        </div>
      </div>
    </div>
  );
};

export default MailBook;
